import { clicksToSound, Metronome } from "./audio/metronome";
import { alignDelay, nextBeatIn } from "./beat-sync";
import { songBpm } from "./bpm";
import { CoachSession } from "./coach-session";
import { CONFIG } from "./config";
import { CountIn } from "./count-in";
import { installDebugHandle } from "./debug-handle";
import { DemoController } from "./demo";
import { CoachDriver } from "./engine/coach-driver";
import { Engine } from "./engine/engine";
import { activeGates, focusDimension, rampedTimingPct } from "./engine/gate-policy";
import type { HandSplitOptions } from "./engine/hand-split";
import type { PlayMode } from "./engine/scoring";
import { MidiInput, pickAutoDevice } from "./midi/input";
import { MidiOutput, pickAutoOutput } from "./midi/output";
import { parseMidi } from "./midi/parser";
import { PlaybackOut } from "./midi/playback-out";
import { MidiRecorder } from "./midi/recorder";
import { PracticePulse } from "./practice-pulse";
import { buildKeyboard, keyboardCal } from "./render/keyboard";
import { render, renderIdle } from "./render/renderer";
import { staffFrame } from "./render/staff";
import { computeStageRects } from "./render/stage-layout";
import {
  attributionLine,
  type CatalogEntry,
  getSongBytes,
  isDownloadable,
  loadCatalog,
} from "./songs/catalog";
import { buildMastery, proficiency } from "./songs/curriculum-state";
import { combinedBaseline, masteryFadeOpacity } from "./songs/highway-fade";
import { loadMapLayout, type MapLayout } from "./songs/map-layout";
import { barChordAtomsDense } from "./songs/mir";
import { readingViewOnLoad } from "./songs/reading";
import { buildSongLattice } from "./songs/song-lattice";
import { resolveDefaultSong } from "./songs/song-pick";
import { planUpload } from "./songs/upload-plan";
import {
  type Calibration,
  loadCalibration,
  loadDeviceId,
  saveCalibration,
  saveDeviceId,
} from "./store/calibration";
import { loadCoachConfig, saveCoachConfig } from "./store/coach-config";
import {
  loadCoachProgress,
  loadCoachRuntime,
  masteredSongIds,
  saveCoachProgress,
} from "./store/coach-progress";
import { type DisplayOptions, loadPrefs, savePrefs, type UIMode } from "./store/prefs";
import { clearScores, loadScores, recordFromStats, saveScore } from "./store/scores";
import { cachedIds, removeCached } from "./store/song-cache";
import { loadOverride, saveOverride } from "./store/song-overrides";
import type { HandFilter } from "./types";
import { createControls } from "./ui/controls";
import { DiscoveryMap } from "./ui/discovery-map";
import { createMidiWarning } from "./ui/midi-warning";
import { planSongPath } from "./ui/path-layout";
import { PianoControl } from "./ui/piano-control";
import { createRecordBadge } from "./ui/record-control";
import { createToast } from "./ui/toast";
import { showUploadCard } from "./ui/upload";

// App entry: wires parser → engine → renderer, with the Web MIDI input feeding
// the wait-mode comparison. The RAF loop is the single monotonic clock.

const stage = mustGet<HTMLDivElement>("stage");
const canvas = mustGet<HTMLCanvasElement>("canvas");
const controlsHost = mustGet<HTMLDivElement>("controls");
const ctx = canvas.getContext("2d");
if (!ctx) throw new Error("Canvas 2D context unavailable.");

const cal: Calibration = loadCalibration();
const midi = new MidiInput();
// MIDI-out playback (F1): a sink driven once per RAF frame by the desired send-set
// (engine.soundingOut). No in-app synth — notes sound on the keyboard's own engine.
const midiOut = new MidiOutput();
const playbackOut = new PlaybackOut(midiOut);
let engine: Engine | null = null;
let coach: CoachDriver | null = null;
/** True while the Coach is auto-paused for inactivity (a no-input pass) — a key
 *  press resumes it. Distinct from the user's pause/exit. */
let coachIdlePaused = false;
const metronome = new Metronome();
let currentBars: number[] = [];
let currentBeats: number[] = [];
/** Free-run metronome for when the song is paused — pulses at the song's own beat
 *  period + meter, so the click stays in time when playback drops in. Null until a
 *  song with a usable beat grid is loaded. */
let pulse: PracticePulse | null = null;
/** Last frame's playhead — the (prev, now] span the metronome clicks across. */
let prevPlayhead = 0;
/** The pulse sounded a beat on the previous frame — so the playhead must not re-sound that
 *  same (phase-aligned) beat as it takes over, which would flam a frame later. */
let pulseJustClicked = false;
/** Music-seconds to hold the playhead after a wait gate completes, so the song drops back
 *  in on a metronome click (the click is the master timebase — it never re-phases). The
 *  hold feeds the engine a dt of 0: the note is already judged, only the clock waits. */
let beatHold = 0;
const countIn = new CountIn();
let coachSession: CoachSession | null = null;
let lastCoachStatus = "";
let lastModeLabel = "";
let catalogEntries: CatalogEntry[] = [];
let mapLayout: MapLayout = { nodes: [], edges: [] };
let currentSong = { id: "", title: "" };
/** Catalog key tag for staff spelling (undefined for imports → inferred). */
let currentKeyHint: string | undefined;
/** Per-bar chord atoms (null = no chord) for the lead-sheet symbol layer. */
let currentChords: (string | null)[] = [];
/** Attribution/source line for the drawer (song detail is appended by reportSong). */
let currentAttribution = "";

/** The Coach drives scored mode under the hood; other UI modes map straight through. */
const engineMode = (m: UIMode): PlayMode => (m === "coach" ? "scored" : m);

/** Persist the current coach's full context (mastery + tempo/phase/window) for the
 *  current song (no-op otherwise). */
function saveCoachProgressNow(): void {
  if (coach && currentSong.id) {
    const snap = coach.snapshot();
    saveCoachProgress(currentSong.id, snap.bars, Date.now(), {
      passages: snap.passages,
      active: snap.active,
    });
  }
}

/** (Re)attach the coach for the current song/config, resuming saved context —
 *  mastery AND the tempo ramp / window position, so re-entering a song continues
 *  the climb instead of restarting at the floor. */
function attachCoach(): void {
  if (engine && currentMode === "coach") {
    const barCount = Math.max(1, currentBars.length - 1);
    const now = Date.now();
    const bars = loadCoachProgress(currentSong.id, barCount, currentCoachConfig, now);
    const runtime = bars
      ? loadCoachRuntime(currentSong.id, barCount, currentCoachConfig, now)
      : null;
    const resume = bars
      ? { bars, passages: runtime?.passages, active: runtime?.active }
      : undefined;
    coach = new CoachDriver(engine, currentBars, currentCoachConfig, 1, resume);
    // Scorecard: gate on the active subset (proficiency baseline ∪ this lesson's focus).
    const entry = catalogEntries.find((e) => e.id === currentSong.id);
    const tags = [...(entry?.teaches ?? []), ...(entry?.uses ?? [])];
    const prof = proficiency(catalogEntries, masteredSongIds());
    coach.activeGates = activeGates(prof, focusDimension(tags));
    coach.timingPct = rampedTimingPct(
      prof,
      currentCoachConfig.masteryTimingFloorPct,
      currentCoachConfig.masteryTimingFullProf,
      currentCoachConfig.masteryTimingPct,
    );
    // The reading crutch baseline the Coach feeds back from (1 = non-reading, inert).
    coach.baselineOpacity = currentDisplay.highwayOpacity;
  } else {
    coach = null;
  }
  lastCoachStatus = "";
  coachIdlePaused = false;
  coachSession?.reset();
}
// Current control selections, applied to each newly-loaded engine so loading a
// song doesn't silently reset mode/tempo/hand back to defaults. Seeded from
// persisted prefs and saved on every change.
const prefs = loadPrefs();
let currentMode: UIMode = prefs.mode;
/** The mode to persist + restore on coach exit; never "coach" (coach is a
 *  per-session opt-in, see store/prefs.ts). */
let lastManualMode: UIMode = currentMode === "coach" ? "wait" : currentMode;
let currentTempo = prefs.tempo;
let currentTranspose = prefs.transpose;
let currentCoachConfig = loadCoachConfig();
let currentHand: HandFilter = prefs.hand;
let currentDisplay = prefs.display;
let currentCountIn = prefs.countInBeats;
let currentLiveKeys = prefs.pianoLiveKeys;
let currentAccompaniment = prefs.accompaniment;
/** While "play it for me" is running (auto mode), the manual mode to revert to on
 *  stop/completion; null when not active. */
let playForMePrev: UIMode | null = null;
/** Last catalog song id (persisted) — re-loaded on startup; file imports clear it. */
let lastCatalogSongId = prefs.lastSongId;
/** The user has interactively chosen a song this session (pick/play-next/import) — as
 *  opposed to the startup restore of `lastSongId`, which is not a live choice and so
 *  still demos on a no-MIDI boot. Read only by the boot reconcile; see
 *  `DemoController.reconcileAtBoot`. */
let userPicked = false;
let abCycleStep = 0;
let lastFrame = 0;
metronome.enabled = prefs.metronome;
metronome.volume = prefs.metronomeVolume;

/** Boot/MIDI state for the first-run demo reel (see DemoController). */
let booted = false;
let midiConnected = false;
const midiWarning = createMidiWarning(document.body);
const toast = createToast(document.body);

// Performance recorder (→ MIDI export). Engine-independent: fed raw musical events from
// MidiInput, exports a .mid on stop. The badge is a fixed DOM overlay.
const recorder = new MidiRecorder();
const recordBadge = createRecordBadge(document.body);
let recordStartMs = 0;
/** The loaded song's base tempo (BPM), for the recording's declared grid; null = free play. */
let currentSongBpm: number | null = null;
midi.onRecord = (e) => {
  if (e.kind === "note") recorder.note(e.pitch, e.on, e.velocity, e.timeMs);
  else recorder.pedal(e.down, e.timeMs);
};

function persistPrefs(): void {
  savePrefs({
    mode: currentMode === "coach" ? lastManualMode : currentMode,
    tempo: currentTempo,
    transpose: currentTranspose,
    hand: currentHand,
    display: currentDisplay,
    metronome: metronome.enabled,
    metronomeVolume: metronome.volume,
    countInBeats: currentCountIn,
    pianoLiveKeys: currentLiveKeys,
    accompaniment: currentAccompaniment,
    lastSongId: lastCatalogSongId,
  });
}

/** Build the free-run pulse from a song's beat grid: the beat period is the first
 *  beat gap, the meter is the beats spanning the first bar. Null when the grid is
 *  too short to derive a period. */
function buildPulse(beats: number[], bars: number[]): PracticePulse | null {
  const a = beats[0];
  const b = beats[1];
  if (a === undefined || b === undefined || b <= a) return null;
  const period = b - a;
  const bar0 = bars[0];
  const bar1 = bars[1];
  const beatsPerBar =
    bar0 !== undefined && bar1 !== undefined && bar1 > bar0
      ? Math.max(1, Math.round((bar1 - bar0) / period))
      : 4;
  return new PracticePulse(period, beatsPerBar);
}

/** Arm the beat-aligned drop-in; returns true if a pre-roll started (caller stays paused).
 *  The click is the master timebase — it never re-phases, so instead hold playback until
 *  starting it puts the song's next beat *on* a click, plus any count-in beats. With the
 *  metronome off there is no click to align to, so playback starts immediately. */
function armBeatStart(): boolean {
  if (!engine || !metronome.enabled || !pulse) return false;
  const period = pulse.period;
  const align = alignDelay(
    pulse.timeToNextClick(),
    nextBeatIn(engine.playhead, currentBeats, period),
    period,
  );
  // Count-in beats are a song-start affordance (as before); a mid-song unpause just aligns.
  const beats = currentMode === "coach" || engine.playhead >= 0 ? 0 : currentCountIn;
  const delay = align + beats * period;
  if (delay <= 0) return false;
  return countIn.start(delay);
}

function applyRotation(): void {
  document.body.classList.toggle("rotated", currentDisplay.rotate180);
}

/** The MIDI-out send-set this frame: the whole song while "play it for me"/demo runs
 *  (auto mode), the unplayed hand while accompaniment is on in scored/coach, else
 *  nothing (paused, wait mode, or feature off → the diff note-offs anything sounding). */
function desiredOut(eng: Engine): { midi: number; velocity: number }[] {
  if (!eng.playing) return [];
  if (eng.mode === "auto") return eng.soundingOut("none");
  if (currentAccompaniment && eng.mode === "scored") return eng.soundingOut(eng.hand);
  return [];
}

/** "Play it for me": listen-through of the current song in the non-judging auto mode,
 *  sounded via MIDI-out. Remembers the manual mode to revert to. */
function startPlayForMe(): void {
  if (!engine) return;
  if (playForMePrev === null) playForMePrev = currentMode;
  coachSession?.cancel();
  // Detach the Coach driver: in auto mode the frame loop must NOT run coach.update(),
  // which would re-seek/clamp the playhead and override tempo, fighting the listen-through
  // (and the auto-completion handler lives in the `else` of `if (coach)`, so it could never
  // even end). Persist first so endPlayForMe can resume the coach exactly where it was.
  saveCoachProgressNow();
  coach = null;
  coachIdlePaused = false;
  countIn.cancel();
  beatHold = 0;
  engine.setMode("auto"); // resets the playhead to the lead-in
  engine.tempo = currentTempo; // honour the tempo multiplier (loop ignored — it's a listen)
  engine.playing = true;
  prevPlayhead = engine.playhead;
  controls.setPlaying(true);
  controls.setStatus("Playing");
}

/** Stop a "play it for me" listen-through and revert to the prior manual mode. */
function endPlayForMe(): void {
  if (playForMePrev === null || !engine) return;
  const prev = playForMePrev;
  playForMePrev = null;
  engine.setMode(engineMode(prev));
  if (prev === "coach") attachCoach(); // rebuild the driver we detached for the listen-through
  playbackOut.silence();
  controls.setPlaying(false);
  controls.setStatus("Ready");
}

function togglePlay(): void {
  if (!engine) return;
  if (playForMePrev !== null) {
    endPlayForMe();
    return;
  }
  // In coach mode there is no paused state — a play/pause gesture exits the loop.
  if (currentMode === "coach") {
    coachSession?.toggle();
    return;
  }
  coachSession?.cancel();
  if (engine.playing || countIn.active) {
    engine.playing = false;
    countIn.cancel();
    beatHold = 0;
    // The pulse is already running (it covers every non-sweeping state, not just pause),
    // so resetting here would restart its phase mid-stream — an audible stumble in a click
    // that should simply carry on. Pause stops the notes, not the beat.
    saveCoachProgressNow();
    controls.setPlaying(false);
    controls.setStatus("Paused");
    return;
  }
  // Already played to the end → a play press restarts from the top. Otherwise the
  // playhead sits past the end, so play() does nothing and nothing falls (the D2
  // "dead after one play-through" bug). Must run before startCountIn (which no-ops
  // once the playhead is ≥ 0).
  if (engine.playhead >= engine.duration) engine.restart();
  metronome.resume();
  if (!armBeatStart()) engine.playing = true;
  controls.setPlaying(true);
  controls.setStatus("Playing");
}

/** Low-level engine start/stop for the coach loop (no count-in, no exit
 *  semantics) — kept distinct from the user's togglePlay so the between-lesson
 *  countdown pause doesn't read as "exit coach". */
function setEnginePlaying(on: boolean): void {
  if (!engine) return;
  if (on) {
    metronome.resume();
    // Drop in on a beat here too. The Coach never goes through togglePlay (that gesture
    // exits the loop), so without this its drills started unaligned and the click stumbled
    // where the playhead crossed into the grid — 934ms against a 1333ms drill beat.
    if (!armBeatStart()) engine.playing = true;
    controls.setPlaying(true);
  } else {
    engine.playing = false;
    countIn.cancel();
    beatHold = 0;
    saveCoachProgressNow();
    controls.setPlaying(false);
  }
}

/** Resume the Coach after an inactivity auto-pause (next key press). The drill
 *  picks up where it left off — tempo and window preserved (the no-input pass
 *  earned no penalty). */
function resumeCoachFromIdle(): void {
  coachIdlePaused = false;
  coach?.clearIdle();
  setEnginePlaying(true);
}

/** Apply a play mode (drawer select + (de)attach coach + pause); shared by the
 *  Mode select and the C♯2 coach toggle. */
function applyMode(mode: UIMode): void {
  saveCoachProgressNow();
  playForMePrev = null; // a real mode choice ends any listen-through
  currentMode = mode;
  if (mode !== "coach") lastManualMode = mode;
  if (engine) engine.setMode(engineMode(mode));
  attachCoach();
  controls.setMode(mode);
  controls.setPlaying(false);
  persistPrefs();
}

function applyTempo(multiplier: number): void {
  currentTempo = Math.max(0.25, Math.min(1.5, multiplier));
  if (engine) engine.tempo = currentTempo;
  controls.setTempo(currentTempo);
  persistPrefs();
}

// ── Performance recording (→ MIDI export) ──────────────────────────────────
function toggleRecord(): void {
  if (recorder.recording) stopRecording();
  else startRecording();
}

function startRecording(): void {
  if (!midiConnected || recorder.recording) return; // need a device to capture
  recordStartMs = performance.now();
  recorder.start(recordStartMs);
  controls.setRecording(true, midiConnected);
}

/** Stop + finalize: download the captured performance as a .mid (nothing if empty). The
 *  declared tempo = the song's effective BPM so real-time notes land on its grid. */
function stopRecording(): void {
  if (!recorder.recording) return;
  recorder.stop(performance.now());
  controls.setRecording(false, midiConnected);
  recordBadge.set(false, "");
  if (recorder.empty) {
    toast.show("Nothing recorded");
    return;
  }
  const name = currentSong.title || "Free play";
  const slug = currentSong.title ? slugify(currentSong.title) : "freeplay";
  const tempoBpm = currentSongBpm ? currentSongBpm * currentTempo : 120;
  downloadMidi(recorder.toBytes({ tempoBpm, name }), `drop-notes-${slug}-${clockStamp()}.mid`);
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "song"
  );
}

function clockStamp(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
}

function downloadMidi(bytes: Uint8Array, filename: string): void {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "audio/midi" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Single entry point for staff-view changes — the drawer today, the Coach later. */
function setStaffView(
  patch: Partial<Pick<DisplayOptions, "staffEnabled" | "staffSplit" | "staffRows" | "staffFollow">>,
): void {
  currentDisplay = { ...currentDisplay, ...patch };
  controls.setDisplay(currentDisplay);
  persistPrefs();
}

/** On load, default the highway crutch to the learner's reading view: a reading
 *  drill is staff-only (highway faded); every other song gets the graded fade
 *  suggested by mastered reading skills. Shown as the overridable default — the
 *  drawer slider still wins. See songs/reading.ts. */
function applyReadingView(teaches: CatalogEntry["teaches"]): void {
  const mastered = buildMastery(catalogEntries, masteredSongIds()).masteredAtoms;
  const view = readingViewOnLoad(teaches, mastered);
  // Memory axis: how much of THIS song the learner has mastered (persisted per-bar).
  const barCount = Math.max(0, currentBars.length - 1);
  const saved = loadCoachProgress(currentSong.id, barCount, currentCoachConfig, Date.now());
  const masteredBars = saved ? saved.filter((b) => b.mastered).length : 0;
  const fraction = barCount > 0 ? masteredBars / barCount : 0;
  const memory = masteryFadeOpacity(fraction);
  // Reading drills stay staff-only (0); other songs take the most-faded of the two axes.
  const baseline = view.forceStaff
    ? 0
    : combinedBaseline(view.highwayOpacity, memory, currentDisplay.challengeFade);
  const patch: Partial<DisplayOptions> = { highwayOpacity: baseline };
  if (view.forceStaff) patch.staffEnabled = true;
  currentDisplay = { ...currentDisplay, ...patch };
  controls.setDisplay(currentDisplay);
  persistPrefs();
  if (coach) coach.baselineOpacity = baseline; // the feed walks up from here
}

function restartPassage(): void {
  if (!engine) return;
  countIn.cancel();
  beatHold = 0;
  if (engine.loopStart !== null) engine.seekTo(engine.loopStart);
  else engine.restart();
  prevPlayhead = engine.playhead;
}

function loopCycle(): void {
  if (!engine) return;
  const t = Math.max(0, engine.playhead);
  if (abCycleStep === 0) engine.setLoop(t, engine.loopEnd ?? engine.duration);
  else if (abCycleStep === 1) engine.setLoop(engine.loopStart ?? 0, t);
  else engine.clearLoop();
  abCycleStep = (abCycleStep + 1) % 3;
  controls.setLoop(engine.loopStart, engine.loopEnd);
}

const controls = createControls(controlsHost, {
  onCatalogPick: (id) => {
    userPicked = true;
    void loadCatalogSong(id);
  },
  onPlayNext: (id) => {
    userPicked = true;
    if (!coachSession?.startNow()) void loadCatalogSong(id);
  },
  onFile: (file) => {
    userPicked = true;
    void loadSong(file);
  },
  onPlayPause: togglePlay,
  onRestart: () => {
    countIn.cancel();
    beatHold = 0;
    engine?.restart();
    if (engine) prevPlayhead = engine.playhead;
    controls.setPlaying(false);
  },
  onTempo: (m) => {
    applyTempo(m);
  },
  onLoopSet: (which) => {
    if (!engine) return;
    const t = Math.max(0, engine.playhead);
    if (which === "a") engine.setLoop(t, engine.loopEnd ?? engine.duration);
    else engine.setLoop(engine.loopStart ?? 0, t);
    controls.setLoop(engine.loopStart, engine.loopEnd);
  },
  onLoopClear: () => {
    engine?.clearLoop();
    controls.setLoop(null, null);
  },
  onTranspose: (s) => {
    currentTranspose = s;
    engine?.setTranspose(s);
    persistPrefs();
  },
  onMetronome: (on) => {
    metronome.enabled = on;
    if (on) metronome.resume();
    persistPrefs();
  },
  onMetronomeVolume: (v) => {
    metronome.volume = v;
    metronome.resume();
    metronome.click(false); // audible preview at the new level
    persistPrefs();
  },
  onCountIn: (beats) => {
    currentCountIn = Math.max(0, Math.min(8, Math.round(beats)));
    persistPrefs();
  },
  onAccompaniment: (on) => {
    currentAccompaniment = on;
    if (!on) playbackOut.silence(); // stop any sounding accompaniment immediately
    persistPrefs();
  },
  onPlayForMe: startPlayForMe,
  onToggleRecord: toggleRecord,
  // Selecting Coach enters the running loop (plays); any manual mode applies + exits.
  onMode: (mode) => (mode === "coach" ? coachSession?.enter() : applyMode(mode)),
  onCoachConfig: (patch) => {
    saveCoachProgressNow();
    currentCoachConfig = { ...currentCoachConfig, ...patch };
    saveCoachConfig(currentCoachConfig);
    attachCoach();
  },
  onHand: (hand: HandFilter) => {
    currentHand = hand;
    engine?.setHand(hand);
    persistPrefs();
  },
  onHandOverride: (opts: HandSplitOptions) => {
    if (currentSong.id) saveOverride(currentSong.id, opts);
    engine?.setHandOverride(opts);
  },
  onDevice: (id) => {
    if (!id) return;
    midi.select(id);
    saveDeviceId(id);
    controls.setMidi(midi.ports().find((p) => p.id === id)?.name ?? null);
  },
  onWhiteWidth: (px) => {
    cal.whiteWidthPx = px;
    saveCalibration(cal);
  },
  onOffset: (px) => {
    cal.offsetPx = px;
    saveCalibration(cal);
  },
  onDisplay: (display) => {
    currentDisplay = display;
    applyRotation();
    persistPrefs();
  },
  onPianoLiveKeys: (on) => {
    currentLiveKeys = on;
    piano.setLiveKeysEnabled(on);
    persistPrefs();
  },
  onClearResults: () => {
    clearScores();
    controls.setResults(loadScores());
  },
  onOpenMap: () => {
    location.hash = "map"; // route → hashchange opens the map (deep-linkable as #map)
  },
  onShowPath: () => {
    location.hash = "path"; // route → hashchange shows the path view (deep-linkable as #path)
  },
});
controls.setCalibration(cal.whiteWidthPx, cal.offsetPx);
controls.setMode(currentMode);
controls.setTempo(currentTempo);
controls.setTranspose(currentTranspose);
controls.setMetronome(metronome.enabled);
controls.setMetronomeVolume(metronome.volume);
controls.setCountIn(currentCountIn);
controls.setAccompaniment(currentAccompaniment);
controls.setRecording(false, midiConnected); // disabled until a MIDI device is present
controls.setCoachConfig(currentCoachConfig);
controls.setHand(currentHand);
controls.setDisplay(currentDisplay);
controls.setResults(loadScores());
applyRotation();

const MAP_HASH = "#map";
const PATH_HASH = "#path";
const discoveryMap = new DiscoveryMap({
  catalog: () => catalogEntries,
  layout: () => mapLayout,
  masteredIds: () => masteredSongIds(),
  loadSong: (id) => void loadCatalogSong(id),
  showPath: (id) => showPathFor(id),
  // Closing drops the #map/#path route without adding a history entry.
  onClose: () => {
    if (location.hash === MAP_HASH || location.hash === PATH_HASH) {
      history.replaceState(null, "", location.pathname + location.search);
    }
  },
});
// One overlay, two data sources: `#map` opens the full map; `#path` re-uses it in
// path mode (openPath). The hash is the single source of truth.
function syncFromHash(): void {
  if (location.hash === MAP_HASH) {
    if (catalogEntries.length > 0) discoveryMap.open();
  } else if (location.hash === PATH_HASH) {
    showPath();
  } else if (discoveryMap.isOpen) {
    discoveryMap.close();
  }
}
window.addEventListener("hashchange", syncFromHash);

// The path view IS the discovery map fed a linear path layout (openPath) — a catalog
// song (incl. LOCKED ones) routes to its node; an uploaded file via planUpload.
function showPathFor(songId: string): void {
  if (catalogEntries.length === 0) return;
  const isCatalog = catalogEntries.some((e) => e.id === songId);
  const upload =
    !isCatalog && engine ? { notes: engine.song.notes, bars: engine.song.bars } : undefined;
  const planned = planSongPath(songId, catalogEntries, upload);
  if (!planned) {
    controls.setStatus("No path — this song needs skills we don't teach yet.");
    return;
  }
  discoveryMap.openPath(planned.layout);
  if (planned.note) controls.setStatus(planned.note);
}
function showPath(): void {
  if (currentSong.id || engine) showPathFor(currentSong.id);
}

// BYO upload: after the learner loads their own .mid, show its deterministic analysis card
// (key/chords/progression/texture/est-difficulty) with a "Show my path" action. Best-effort —
// a malformed catalog/lattice just skips the card.
function showUploadAnalysis(): void {
  if (!engine || catalogEntries.length === 0) return;
  try {
    const lattice = buildSongLattice(catalogEntries);
    const drilled = new Set(catalogEntries.flatMap((e) => e.teaches ?? []));
    const plan = planUpload(engine.song.notes, engine.song.bars, lattice, drilled);
    showUploadCard(plan, () => showPathFor(currentSong.id));
  } catch {
    /* analysis is optional; skip on any lattice/catalog issue */
  }
}

const piano = new PianoControl({
  menu: controls.menu,
  liveKeysEnabled: () => currentLiveKeys,
  restart: () => {
    restartPassage();
    controls.setPlaying(engine?.playing ?? false);
  },
  loopCycle,
  playPause: togglePlay,
  nudgeTempo: (delta) => applyTempo(currentTempo + delta),
  toggleCoach: () => coachSession?.toggle(),
  playForMe: startPlayForMe,
  toggleMetronome: () => {
    metronome.enabled = !metronome.enabled;
    if (metronome.enabled) metronome.resume();
    controls.setMetronome(metronome.enabled);
    persistPrefs();
  },
  toggleRecord,
  onEnterCommandMode: () => {
    if (engine) engine.playing = false;
    countIn.cancel();
    beatHold = 0;
    coachSession?.cancel();
    controls.setPlaying(false);
  },
  onNav: (key) => {
    if (!discoveryMap.isOpen) return false;
    discoveryMap.handleNav(key);
    return true;
  },
  onExitCommandMode: () => {
    if (discoveryMap.isOpen) discoveryMap.close();
  },
});
midi.setNoteFilter((pitch, on, timeMs) => {
  // On a MIDI-only bench (no pointer/keydown) playing a key is the only interaction,
  // so try the audio unlock here too — the window-gesture listeners never fire there.
  if (on) metronome.resume();
  // A mastered lesson is waiting on the "press any key to continue" gate — the first
  // note-on passes it and loads the next lesson (consumed, so it doesn't bleed in).
  if (on && coachSession?.startNow()) return true;
  const suppressed = piano.note(pitch, on, timeMs);
  // The Coach auto-paused after a no-input pass (you stepped away) — the first real
  // music note (not a command/live-strip key) resumes it, consumed so it isn't
  // judged as an early/wrong press against the restarted drill.
  if (on && !suppressed && coachIdlePaused) {
    resumeCoachFromIdle();
    return true;
  }
  return suppressed;
});
controls.setPianoLiveKeys(currentLiveKeys);

coachSession = new CoachSession({
  hasSong: () => engine !== null,
  isPlaying: () => engine?.playing ?? false,
  coachDone: () => coach?.done ?? false,
  mode: () => currentMode,
  catalog: () => catalogEntries,
  applyMode,
  loadSong: loadCatalogSong,
  setPlaying: setEnginePlaying, // low-level: the loop's own start/stop, not a user gesture
  setStatus: controls.setStatus,
  setPlayNext: controls.setPlayNext,
});

// First-run attract reel: with no MIDI, auto-play a looping demo; on connect (or
// boot-with-MIDI) load the saved/default song. A demo song is "not a user song"
// because it runs in engine mode "auto".
const demo = new DemoController({
  catalog: () => catalogEntries,
  hasUserSong: () => engine !== null && engine.mode !== "auto",
  startAuto: (id) => {
    void startDemoSong(id);
  },
  loadUserSong: () => {
    void restoreUserSong();
  },
});

function loadBuffer(
  buffer: ArrayBuffer,
  name: string,
  attribution: string,
  id: string,
  title: string,
): void {
  saveCoachProgressNow(); // persist the outgoing song's coach before swapping
  playForMePrev = null; // a new song ends any listen-through
  playbackOut.silence();
  const song = parseMidi(buffer, name);
  currentBars = song.bars;
  currentBeats = song.beats;
  currentChords = barChordAtomsDense(song.notes, song.bars); // lead-sheet symbol layer
  pulse = buildPulse(currentBeats, currentBars);
  countIn.cancel();
  beatHold = 0;
  engine = new Engine(song);
  prevPlayhead = engine.playhead;
  // Carry the current control selections onto the fresh engine.
  engine.mode = engineMode(currentMode);
  engine.tempo = currentTempo;
  engine.transpose = currentTranspose;
  engine.setHand(currentHand);
  const override = loadOverride(id);
  engine.setHandOverride(override);
  controls.setHandOverride(override);
  currentSong = { id, title };
  controls.setCurrentSong(title);
  controls.setSelectedSong(id); // keep the drawer picker in sync (incl. reload restore)
  // Attach the Coach last, once song id + note model (hand/transpose) are final.
  attachCoach();
  controls.setPlaying(false);
  controls.setLoop(engine.loopStart, engine.loopEnd);
  // The song name lives in the catalog dropdown; show only attribution/source
  // in the drawer and keep the top-bar status to playback info. reportSong owns
  // the now-playing line (attribution + song detail).
  currentAttribution = attribution;
  currentSongBpm = songBpm(song); // declared grid for a recording made against this song
  controls.setSongBpm(currentSongBpm);
  controls.setTempo(currentTempo); // re-render the readout in BPM now the base is known
  reportSong(song.duration);
  coachSession?.refreshPlayNext();
}

async function loadSong(file: File): Promise<void> {
  try {
    demo.stop(); // a file import leaves the demo reel
    currentKeyHint = undefined; // imports have no catalog key tag → inferred
    lastCatalogSongId = ""; // a file import can't be restored on reload (bytes not kept)
    persistPrefs();
    loadBuffer(await file.arrayBuffer(), file.name, file.name, `file:${file.name}`, file.name);
    applyReadingView(undefined); // an import is an apply song → graded reading fade
    showUploadAnalysis(); // BYO: surface the detected analysis + path for the loaded song
  } catch (err) {
    controls.setStatus(`Failed to load ${file.name}: ${String(err)}`);
  }
}

async function loadCatalogSong(
  id: string,
  opts?: { persist?: boolean; shouldApply?: () => boolean },
): Promise<void> {
  const persist = opts?.persist !== false;
  const entry = catalogEntries.find((e) => e.id === id);
  if (!entry) return;
  if (persist) {
    // A user pick leaves the demo reel: stop it (so any in-flight demo load
    // self-cancels via its shouldApply) and drop to the loading screen until the
    // pick lands. (The demo's own loads are persist:false and don't do this.)
    if (demo.isActive) {
      demo.stop();
      engine = null;
    }
    lastCatalogSongId = entry.id;
    persistPrefs(); // remember this song so a reload restores it
  }
  currentKeyHint = entry.key;
  const downloading = isDownloadable(entry);
  if (downloading) controls.setStatus("Downloading…");
  try {
    const buffer = await getSongBytes(entry);
    if (opts?.shouldApply && !opts.shouldApply()) return; // superseded (demo left)
    loadBuffer(buffer, entry.title, attributionLine(entry), entry.id, entry.title);
    // Real picks default the reading crutch; the demo reel (persist:false) owns
    // its own transient display via effectiveDisplay() — don't disturb it.
    if (persist) applyReadingView(entry.teaches);
    if (downloading) controls.setCached(await cachedIds());
  } catch (err) {
    if (downloading) {
      await removeCached(entry.id);
      controls.setStatus(`Couldn't download ${entry.title} — check connection or source.`);
    } else {
      controls.setStatus(`Failed to load ${entry.title}: ${String(err)}`);
    }
  }
}

/** Demo reel: load a catalog song into auto mode and play it immediately (never
 *  persisted as the last song — it's not the user's choice). */
async function startDemoSong(id: string): Promise<void> {
  await loadCatalogSong(id, { persist: false, shouldApply: () => demo.isActive });
  if (!demo.isActive || !engine) return; // a real pick took over the reel
  engine.mode = "auto";
  engine.playing = true;
  prevPlayhead = engine.playhead;
  controls.setPlaying(true);
  controls.setStatus("Demo");
}

/** Load the configured starter song (or the simplest fallback), paused in wait. */
async function loadDefaultSong(): Promise<void> {
  const entry = resolveDefaultSong(catalogEntries);
  if (entry) await loadCatalogSong(entry.id);
}

/** Leave the demo reel for the user's song: the last one played (survives the demo's
 *  persist:false loads, so Coach progress resumes), else the default starter. */
async function restoreUserSong(): Promise<void> {
  if (lastCatalogSongId && catalogEntries.some((e) => e.id === lastCatalogSongId)) {
    await loadCatalogSong(lastCatalogSongId);
    return;
  }
  await loadDefaultSong();
}

function reportSong(duration: number): void {
  if (!engine) return;
  // Count notes that fall outside the covered range — drop + warn.
  const kb = buildKeyboard(canvas.clientWidth, cal);
  const outOfRange = engine.notes.filter((n) => !kb.inRange(n.midi)).length;
  // Keep the top-bar status terse; song detail (length, dropped notes) lives in
  // the drawer's now-playing line next to the attribution.
  const detail = `${duration.toFixed(0)}s${outOfRange > 0 ? ` · ${outOfRange} notes out of range` : ""}`;
  controls.setNowPlaying(currentAttribution ? `${currentAttribution} · ${detail}` : detail);
  controls.setStatus("Ready");
}

async function initCatalog(): Promise<void> {
  catalogEntries = await loadCatalog();
  mapLayout = await loadMapLayout();
  controls.setCatalog(catalogEntries);
  controls.setCached(await cachedIds());
  coachSession?.refreshPlayNext();
  // Restore the last song so a reload lands where you left off (and shows the
  // staff). Guarded by a catalog lookup so a song dropped in a newer version
  // just falls back to the idle screen instead of erroring. Awaited so the boot
  // reconcile sees the loaded song (and skips the demo) rather than racing it.
  if (lastCatalogSongId && catalogEntries.some((e) => e.id === lastCatalogSongId)) {
    await loadCatalogSong(lastCatalogSongId);
  }
}

async function initMidi(): Promise<void> {
  try {
    await midi.init();
    midiOut.useAccess(midi.midiAccess()); // share the one MIDIAccess — no second request
    midi.onChange = () => {
      /* held set changes are picked up by the next frame */
    };
    midi.onStateChange(refreshDevices);
    refreshDevices();
  } catch {
    // No Web MIDI (unsupported / denied) — the warning card explains it and the
    // boot reconcile drops into the demo reel.
    midiConnected = false;
    midiWarning.show();
  }
}

function refreshDevices(): void {
  const ports = midi.ports();
  // Auto-select a real instrument, never the ALSA "Midi Through" loopback (it
  // carries no notes). If none is present — e.g. the keyboard is asleep — select
  // nothing and wait, rather than latching onto (and persisting) the loopback.
  const selected = pickAutoDevice(ports, loadDeviceId());
  controls.setDevices(ports, selected);
  if (selected) {
    midi.select(selected);
    saveDeviceId(selected);
  }
  controls.setMidi(ports.find((p) => p.id === selected)?.name ?? null);
  midiConnected = selected != null;
  // Device dropped mid-record → finalize + download what was captured (don't lose it), then
  // the Record button disables until a device returns.
  if (!midiConnected && recorder.recording) stopRecording();
  controls.setRecording(recorder.recording, midiConnected);
  // Auto-pair the output to the selected input by name (one bidirectional device sounds
  // host-sent notes); deselect (and panic) when no input is present.
  midiOut.select(midiConnected ? pickAutoOutput(midiOut.ports(), midi.currentName()) : null);
  if (midiConnected) midiWarning.hide();
  else midiWarning.show();
  if (booted) demo.onMidiChange(midiConnected); // post-boot hot-plug; boot reconciles once
}

// While the demo reel plays (auto mode), override the display + keyboard
// transiently (never persisted): show the staff at 70/30 and fit a fixed
// 3-octave keyboard to the screen. Restored the instant the demo ends.
function effectiveDisplay(): DisplayOptions {
  // While coaching, overlay the Coach's fed highway crutch (transient — never mutates
  // currentDisplay, so exiting coach mode restores the piece baseline automatically).
  if (coach && currentMode === "coach" && coach.highwayOpacity !== currentDisplay.highwayOpacity) {
    return { ...currentDisplay, highwayOpacity: coach.highwayOpacity };
  }
  if (engine?.mode !== "auto") return currentDisplay;
  // Auto playback (demo reel / "Play it for me") guarantees a visible staff, but
  // otherwise respects the user's layout — split/rows/follow track the drawer. Only
  // force a split when the user has the staff off (0) so the reel still shows one.
  return {
    ...currentDisplay,
    staffEnabled: true,
    staffSplit: currentDisplay.staffSplit || 1,
  };
}
function effectiveCal(width: number): Calibration {
  return keyboardCal(cal, width, demo.isActive);
}

function resize(): void {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(stage.clientWidth * dpr);
  canvas.height = Math.floor(stage.clientHeight * dpr);
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function frame(now: number): void {
  // Clamp the frame delta: a refocused backgrounded tab reports a multi-second gap, which
  // would advance the playhead in one giant step (sweeping every skipped note as an unjudged
  // miss) and fire a burst of metronome clicks. The single RAF clock is still the timebase.
  const dt = lastFrame ? Math.min((now - lastFrame) / 1000, CONFIG.maxFrameDt) : 0;
  lastFrame = now;

  // Surface the effective play mode in the top bar (change-detected). Demo (attract reel)
  // and "play it for me" both run the engine in auto; the demo controller distinguishes them.
  const modeLabel = demo.isActive
    ? "Demo"
    : currentMode === "coach"
      ? "Coach"
      : currentMode === "scored" || engine?.mode === "auto"
        ? "Play"
        : "Wait";
  if (modeLabel !== lastModeLabel) {
    controls.setModeLabel(modeLabel);
    lastModeLabel = modeLabel;
  }
  // Per-chain mastery dots: present only while a coach is attached (Coach mode).
  controls.setChainDots(coach ? coach.chainProgress() : []);

  if (recorder.recording) {
    const s = Math.floor((now - recordStartMs) / 1000);
    recordBadge.set(true, `⏺ REC ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
  }

  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (ctx) {
    if (engine) {
      // Real seconds → music-seconds. Use the engine's *own* tempo, never the tempo
      // control: the Coach ramps `engine.tempo` for a slow drill and leaves the control
      // alone, so `currentTempo` would run the click at a different rate than the notes it
      // is counting (a drill at 50% clicked twice as fast the moment the playhead swept).
      const musicDt = dt * engine.tempo;
      // Beat-aligned pre-roll (the pulse clicks through it); flips engine.playing once it
      // elapses, with the song's next beat landing on a click.
      if (countIn.tick(musicDt)) {
        engine.playing = true;
        prevPlayhead = engine.playhead;
      }
      // heldVelocity allocates a Map; the scorer only reads it on a scored-mode press edge,
      // so skip building it in wait/coach/auto.
      //
      // Hold the playhead (dt 0 — judging still runs, only the clock waits) until the song
      // can drop back in on a click; otherwise arm that hold as each wait gate completes. A
      // gate freezes the song while the click runs on, so the grid falls behind by however
      // long you took; the note is accepted immediately either way.
      const holding = beatHold > 0;
      engine.update(
        holding ? 0 : dt,
        midi.held,
        engine.mode === "scored" ? midi.heldVelocity : undefined,
      );
      if (holding) beatHold = Math.max(0, beatHold - musicDt);
      else if (engine.takeGateCompleted() && metronome.enabled && pulse) {
        beatHold = alignDelay(
          pulse.timeToNextClick(),
          nextBeatIn(engine.playhead, currentBeats, pulse.period),
          pulse.period,
        );
      }
      // Metronome: an enabled click never falls silent. Follow the playhead's real grid
      // beats while it sweeps (barline accents), fall back to the free-run pulse whenever it
      // doesn't (paused, the lead-in, a wait gate, the coach's loop/idle) AND to fill any
      // beatless span the sweep crosses (an off-grid drill-window tail past the last beat).
      // See `clicksToSound`. Advance the pulse every frame — it is the click's phase
      // reference; a pulse frozen while the playhead sweeps would resume from a stale phase.
      const pulseBeats = pulse ? pulse.advance(musicDt) : [];
      const sound = clicksToSound({
        enabled: metronome.enabled,
        mode: engine.mode,
        playing: engine.playing,
        prevPlayhead,
        playhead: engine.playhead,
        beats: currentBeats,
        bars: currentBars,
        pulseBeats,
        pulseJustClicked,
      });
      for (const accent of sound.accents) metronome.click(accent);
      pulseJustClicked = sound.pulseJustClicked;
      if (pulse && sound.anchor !== null) pulse.anchor(sound.anchor);
      prevPlayhead = engine.playhead;
      if (coach) {
        coach.update();
        // The Coach owns the tempo while it drills — it ramps `engine.tempo` as a passage is
        // mastered. Track it on the control so the slider and BPM readout say what you
        // actually hear, and so leaving coach keeps the tempo you earned instead of the
        // control silently disagreeing with the engine (it used to claim 100% while the song
        // still ran at the drill's 50%, with nothing to put it back).
        if (engine.tempo !== currentTempo) {
          currentTempo = engine.tempo;
          controls.setTempo(currentTempo);
          persistPrefs();
        }
        if (coach.status !== lastCoachStatus) {
          controls.setStatus(coach.status);
          lastCoachStatus = coach.status;
        }
        // Stepped away: a full pass with no key-presses → pause the loop (no penalty;
        // tempo/window preserved). A key press resumes it via the note filter.
        if (coach.idle && !coachIdlePaused) {
          coachIdlePaused = true;
          setEnginePlaying(false);
        }
      } else if (engine.consumeCompletion()) {
        if (engine.mode === "auto") {
          if (playForMePrev !== null)
            endPlayForMe(); // listen-through finished → revert
          else demo.next(); // attract reel → next piece
        } else finishScoredRun(engine);
      }
      coachSession?.tick(); // gate the loop on a mastered exercise (press-any-key)
      // MIDI-out playback: reconcile the keyboard's synth to the desired send-set once
      // per frame (single clock — no scheduler). silence()/diff handle pause + seeks.
      playbackOut.sync(desiredOut(engine), engine.playhead);
      // In demo (auto) mode there's no real input — light keys from what's sounding.
      const renderHeld = engine.mode === "auto" ? engine.soundingPitches() : midi.held;
      render(
        ctx,
        engine,
        effectiveCal(width),
        renderHeld,
        width,
        height,
        effectiveDisplay(),
        piano.commandKeyLabels(),
        currentKeyHint,
        currentChords,
      );
    } else {
      // No song yet — still draw the keyboard so held keys light up (MIDI check).
      renderIdle(ctx, cal, midi.held, width, height, currentDisplay, piano.commandKeyLabels());
    }
  }
  requestAnimationFrame(frame);
}

function finishScoredRun(eng: Engine): void {
  const record = recordFromStats(
    currentSong.id || "unknown",
    currentSong.title || "Unknown",
    new Date().toISOString(),
    eng.scoreStats,
  );
  saveScore(record);
  controls.setResults(loadScores());
  controls.setPlaying(false);
  controls.setStatus("Ready");
  toast.show(
    `Done — ${Math.round(record.accuracy * 100)}% · ${record.perfect + record.good}/${record.total} hit · ${record.misses} missed`,
  );
}

function mustGet<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id} element.`);
  return node as T;
}

installDebugHandle({
  playhead: () => (engine ? engine.playhead : null),
  playing: () => (engine ? engine.playing : false),
  mode: () => (engine ? engine.mode : null),
  tempo: () => (engine ? engine.tempo : null),
  misses: () => (engine ? engine.scoreStats.misses : 0),
  handCounts: () => (engine ? engine.handCounts() : { left: 0, right: 0, unknown: 0 }),
  coachStatus: () => (coach ? coach.status : null),
  countingIn: () => countIn.active,
  demoActive: () => demo.isActive,
  commandMode: () => piano.commandMode,
  feedNote: (pitch, on, timeMs) => piano.note(pitch, on, timeMs),
  staff: () => {
    const eng = engine;
    if (!eng) return null;
    const w = canvas.clientWidth;
    const disp = effectiveDisplay(); // reflect the demo's transient staff override
    const follow =
      disp.staffFollow === "flow"
        ? "flow"
        : disp.staffFollow === "page"
          ? "page"
          : disp.staffRows === 1
            ? "flow"
            : "page";
    const rects = computeStageRects(w, canvas.clientHeight, {
      staffEnabled: disp.staffEnabled,
      staffSplit: disp.staffSplit,
      flow: follow === "flow",
      whiteWidth: effectiveCal(w).whiteWidthPx,
    });
    const model = staffFrame({
      song: eng.song,
      notes: eng.notes,
      playhead: eng.playhead,
      judgement: (n) => eng.judgementFor(n),
      recentWrong: eng.recentWrong(),
      rect: rects,
      width: w,
      rows: disp.staffRows,
      follow,
      keyHint: currentKeyHint,
      chords: disp.showChordSymbols ? currentChords : undefined,
    });
    return {
      enabled: disp.staffEnabled,
      split: disp.staffSplit,
      rows: disp.staffRows,
      follow: disp.staffFollow,
      noteStates: model.noteheads.map((n) => n.state),
      noteValues: model.noteheads.map((n) => n.value ?? "none"),
      nowMarkerX: model.nowMarker?.x ?? null,
      barlineCount: model.barlines.length,
      beamCount: model.beams.length,
    };
  },
  setStaffView: (patch) => {
    const follow = patch.staffFollow;
    setStaffView({
      ...patch,
      staffFollow: follow === "flow" || follow === "page" || follow === "auto" ? follow : undefined,
    });
  },
  midiOut: () => midiOut.recentOut(),
  audio: () => ({
    state: metronome.audioState,
    enabled: metronome.enabled,
    freeRun: pulse !== null,
    mode: engine ? engine.mode : null,
    playing: engine ? engine.playing : false,
  }),
  beep: () => {
    metronome.resume();
    metronome.click(true);
  },
  recording: () => recorder.recording,
  recordedNotes: () => recorder.noteCount,
});

window.addEventListener("resize", resize);
window.addEventListener("beforeunload", saveCoachProgressNow);
// Web Audio autoplay policy: the metronome's AudioContext can only leave "suspended"
// in response to a user gesture. A live-key toggle (MIDI) is not such a gesture, so
// unlock it on the first real pointer/key interaction with the page.
const unlockAudio = (): void => metronome.resume();
window.addEventListener("pointerdown", unlockAudio, { once: true });
window.addEventListener("keydown", unlockAudio, { once: true });
resize();

async function boot(): Promise<void> {
  await initCatalog(); // restores the saved song if present
  await initMidi(); // selects a device (or fails → no device)
  booted = true;
  // First reconcile: demo reel (no MIDI, no song), default song (MIDI, no song),
  // or nothing (a saved song already loaded, or a pick landed while initMidi resolved —
  // the reel must not open over live user intent). Hot-plugs after this go via
  // refreshDevices and still demo on any device loss.
  demo.reconcileAtBoot(midiConnected, userPicked);
  syncFromHash(); // honour a `#map` deep link now the catalog/layout are loaded
}
void boot();
requestAnimationFrame(frame);
